import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { STATEMENT_VIDEOS, STATEMENT_LINES, posterOf } from "@/data/studio";

const VIDEO_RANGES = [
  [[0, 0.3, 0.4], [1, 1, 0]],
  [[0.3, 0.4, 0.63, 0.73], [0, 1, 1, 0]],
  [[0.63, 0.73, 1], [0, 1, 1]],
];

const Layer = ({ src, range, p }) => {
  const opacity = useTransform(p, range[0], range[1]);
  return <motion.video style={{ opacity }} src={src} poster={posterOf(src)} autoPlay muted loop playsInline preload="metadata" className="absolute inset-0 h-full w-full object-cover" />;
};

const Line = ({ text, i, p }) => {
  const s = 0.24 + i * 0.13;
  const opacity = useTransform(p, [s, s + 0.04, s + 0.09, s + 0.13], [0, 1, 1, 0]);
  const y = useTransform(p, [s, s + 0.04, s + 0.09, s + 0.13], [50, 0, 0, -50]);
  return (
    <motion.p style={{ opacity, y }} data-testid={`studio-statement-line-${i}`}
      className="absolute inset-x-0 mx-auto max-w-4xl px-5 text-center font-display text-3xl font-bold leading-tight text-white sm:text-5xl lg:text-6xl">
      {text}
    </motion.p>
  );
};

export const Statement = () => {
  const ref = useRef(null);
  const { scrollYProgress: p } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const headOpacity = useTransform(p, [0, 0.06, 0.17, 0.23], [0, 1, 1, 0]);
  const headScale = useTransform(p, [0, 0.23], [0.94, 1.04]);
  const endOpacity = useTransform(p, [0.8, 0.87, 1], [0, 1, 1]);
  const endY = useTransform(p, [0.8, 0.87], [50, 0]);

  return (
    <section ref={ref} data-testid="studio-statement" className="relative h-[420vh] bg-sink">
      <div className="sticky top-0 flex h-screen items-center justify-center overflow-hidden">
        {STATEMENT_VIDEOS.map((src, i) => <Layer key={src} src={src} range={VIDEO_RANGES[i]} p={p} />)}
        <div className="absolute inset-0 bg-sink/[0.72]" />
        <motion.div style={{ opacity: headOpacity, scale: headScale }} className="absolute inset-x-0 px-5 text-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand">The New Rules of Production</p>
          <h2 className="mt-6 font-display text-5xl font-extrabold uppercase leading-[0.92] tracking-tight text-white sm:text-7xl lg:text-[8rem]">
            Don't Film<br />What You Can <span className="text-brand">Imagine.</span>
          </h2>
        </motion.div>
        {STATEMENT_LINES.map((t, i) => <Line key={t} text={t} i={i} p={p} />)}
        <motion.div style={{ opacity: endOpacity, y: endY }} className="absolute inset-x-0 px-5 text-center" data-testid="studio-statement-final">
          <p className="font-display text-4xl font-extrabold leading-tight text-white sm:text-6xl lg:text-7xl">AI changes what's possible.</p>
          <p className="mt-6 font-mono text-[12px] uppercase tracking-[0.3em] text-brand">Perfect Digitals turns it into finished video.</p>
        </motion.div>
      </div>
    </section>
  );
};
